import { Theme, useTheme } from "@/components/theme/themeProvider"
import { errorToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import Vector from "@images/background/vector.svg"
import { CongratulationIcon } from "@images/box/congratulationIcon"
import { type AddressAutofillSuggestionResponse } from "@mapbox/search-js-core"
import { useAddressAutofillCore } from "@mapbox/search-js-react"
import { convertAddressToFormik } from "@modules/address/addressFormikType"
import { EmptyAddress } from "@modules/address/addressUtils"
import {
  type BoxFormikType,
  type BoxInputKey,
  convertBoxFormFormikToInputDto,
  getValidationSchema,
} from "@modules/box/boxFormikType"
import { BoxFormFifthPage } from "@modules/box/components/boxCreationForm/boxFormFifthPage"
import { BoxFormFirstPage } from "@modules/box/components/boxCreationForm/boxFormFirstPage"
import { BoxFormFourthPage } from "@modules/box/components/boxCreationForm/boxFormFourthPage"
import { BoxFormLegalDocumentsPage } from "@modules/box/components/boxCreationForm/boxFormLegalDocumentsPage"
import { BoxFormResumePage } from "@modules/box/components/boxCreationForm/boxFormResumePage"
import { BoxFormSecondPage } from "@modules/box/components/boxCreationForm/boxFormSecondPage"
import { BoxFormThirdPage } from "@modules/box/components/boxCreationForm/boxFormThirdPage"
import { BoxFormWelcomePage } from "@modules/box/components/boxCreationForm/boxFormWelcomePage"
import { BoxOnboardingWrapper } from "@modules/box/components/boxOnboardingWrapper"
import { useCreateBox } from "@modules/box/hooks/useBoxActions"
import { Config } from "@modules/config"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage, isFormFieldInValid } from "@modules/formik/formikUtils"
import { useBoxNavigate } from "@modules/navigation/hooks/useBoxNavigate"
import { Path } from "@modules/navigation/routes"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { LoggerType } from "@modules/utils/loggerUtils"
import dayjs from "dayjs"
import { useFormik } from "formik"
import { useEffect, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import styled from "styled-components"

export function BoxFormCreation() {
  const { createBox, isPending: isCreatingBox } = useCreateBox()
  const [, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const boxNavigate = useBoxNavigate()
  const { setTheme, theme } = useTheme()
  const [pageNumber, setPageNumber] = useState(1)
  const [displayWelcomePage, setDisplayWelcomePage] = useState(true)
  const popup = useConfirmPopup()
  const autofill = useAddressAutofillCore({
    accessToken: Config.MAPBOX_API_KEY,
  })

  useEffect(() => {
    if (theme !== Theme.OwnerDark) {
      setTheme(Theme.OwnerDark)
    }
  }, [])

  const navigateForm = (onNext: boolean) => {
    setPageNumber((prev) => (onNext ? prev + 1 : prev - 1))
  }

  useEffect(() => {
    setSearchParams({ page: pageNumber.toString() })
  }, [pageNumber])

  const createdPopup = (newBoxId: string) =>
    popup.show({
      title: <CongratulationIcon color="" className="mx-auto" />,
      message: (
        <div className="flex flex-col items-center gap-2 text-center">
          <Text fontStyle="TL">{word("box.form.create.modal.title")}</Text>
          <Text fontStyle="BM" className="text-neutral-600">
            {word("box.form.create.modal.description")}
          </Text>
        </div>
      ),
      accept: () => {
        return Promise.resolve(void boxNavigate(Path.Box.OnboardingStripe, { newBoxId }))
      },
      footerProps: {
        confirmText: word("box.form.create.modal.nextStep"),
      },
      classNames: {
        dialogFooterCancel: "hidden",
        dialogFooterConfirm: "flex-1",
        dialogFooter: "flex flex-row justify-around w-full",
      },
      dialogContentProps: {
        showCloseButton: false,
      },
      dialogProps: {
        onOpenChange: () => {
          return
        },
      },
    })

  const validationSchema = useMemo(() => getValidationSchema(pageNumber), [pageNumber])

  const formik = useFormik<BoxFormikType>({
    initialValues: {
      name: "",
      ...convertAddressToFormik(EmptyAddress),
      phoneNumber: "",
      phoneNumberCountryCode: "",
      email: "",
      logo: null,
      cover: null,
      boxPhotos: [],
      siren: "",
      description: "",
      location: null,
      zoneId: dayjs.tz.guess(),
    },
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values) => {
      if (pageNumber < 7) {
        if (pageNumber === 2) {
          const res: AddressAutofillSuggestionResponse = await autofill.suggest(
            values.address + " " + values.postalCode + " " + values.city,
            {
              sessionToken: undefined,
            },
          )

          if (
            res.suggestions.length > 0 &&
            res.suggestions.find(
              (suggestion) =>
                suggestion.address_line1?.toLowerCase() === values.address.toLowerCase() &&
                suggestion.address_level2?.toLowerCase() === values.city.toLowerCase() &&
                (suggestion.postcode?.toLowerCase() === values.postalCode.toLowerCase() ||
                  suggestion.match_code.postcode === "not_applicable") &&
                (suggestion.country_code?.toLowerCase() === values.country.toLowerCase() ||
                  ("iso_3166_2" in suggestion.metadata &&
                    typeof suggestion.metadata.iso_3166_2 === "string" &&
                    suggestion.metadata.iso_3166_2?.toLowerCase() === values.country.toLowerCase())),
            )
          ) {
            navigateForm(true)
          } else {
            toast.error(word("global.error.label"), {
              description: word("form.address.error.select"),
              classNames: errorToastClassName(),
            })
            formik.setFieldError("address", word("form.address.error.invalid"))
          }
        } else {
          navigateForm(true)
        }
        return
      }
      await createBox(convertBoxFormFormikToInputDto(values), {
        onSuccess: ({ id }) => {
          createdPopup(id)
        },
        onError: (error) => {
          console.error(LoggerType.Box + "create", error)
          toast.error(word("global.error.label"), {
            classNames: errorToastClassName(),
          })
        },
      })
    },
  })

  // Reset the form when the page is refreshed
  useEffect(() => {
    setPageNumber(1)
    setSearchParams({ page: "1" })
    formik.resetForm()
  }, [])

  const handleClose = () => {
    popup.show({
      title: word("box.creation.form.cancel.title"),
      message: word("box.creation.form.cancel.description"),
      accept: () => {
        setTimeout(() => {
          void navigate(Path.Root)
        }, 100)
        setTheme(Theme.AthleteLight)
      },
    })
  }

  const FormPage = () => {
    const props = {
      handleSubmit: formik.handleSubmit,
      onPrevious: pageNumber === 1 ? () => setDisplayWelcomePage(true) : () => navigateForm(false),
      isFormFieldInValid: (input: BoxInputKey) => isFormFieldInValid(input, formik),
      getFormErrorMessage: (input: BoxInputKey) => getFormErrorMessage(input, formik),
      isSubmitting: formik.isSubmitting || isCreatingBox,
      disabled: !getValidationSchema(pageNumber).isValidSync(formik.values),
      formik: formik,
    }
    switch (pageNumber) {
      case 1:
        return <BoxFormFirstPage {...props} />
      case 2:
        return <BoxFormSecondPage {...props} />
      case 3:
        return <BoxFormThirdPage {...props} />
      case 4:
        return <BoxFormFourthPage {...props} />
      case 5:
        return <BoxFormFifthPage {...props} />
      case 6:
        return <BoxFormLegalDocumentsPage {...props} />
      case 7:
        return <BoxFormResumePage {...props} />
      default:
        return null
    }
  }

  return (
    <BoxOnboardingWrapper backgroundImage={Vector} handleClose={handleClose}>
      <CustomForm
        onSubmit={formik.handleSubmit}
        className="flex h-full flex-col items-center justify-between gap-3 p-12"
      >
        {displayWelcomePage ? <BoxFormWelcomePage onClick={() => setDisplayWelcomePage(false)} /> : FormPage()}
      </CustomForm>
    </BoxOnboardingWrapper>
  )
}

const CustomForm = styled.form`
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  textarea:-webkit-autofill,
  textarea:-webkit-autofill:hover,
  textarea:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 1000px transparent inset;
    -webkit-text-fill-color: white;
    transition: background-color 5000s ease-in-out 0s;
  }
`
